import { useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { ATTACHMENT_TYPES, ATTACHMENTS_MAX, attachmentPath, fileSize, runCommunicationCommand, uploadAttachment } from '../../api/communication';
import type { Attachment, AttachmentOwner, CommunicationResult, Person, Tone, TypeRef } from '../../api/communication';
import { downloadFile } from '../../api/document';
import type { Translate } from '../../i18n';
import { useMe, useSession } from '../../session/useSession';
import { Avatar } from '../controls';
import { LIMITS, fieldErrors, problemMessage } from '../form';
import { intlLocale } from '../format';
import { Icon } from '../Icon';

export type Errors = Readonly<Record<string, string>>;

/** Ce que le compte connecté peut faire dans la communication. Pour afficher ; le serveur décide. */
export interface ComAccess {
    readonly chat: boolean;
    readonly notify: boolean;
    readonly manage: boolean;
    /** Un étudiant : il reçoit et répond, il n'écrit jamais le premier. */
    readonly student: boolean;
}

export function useComAccess(): ComAccess {
    const me = useMe();
    const has = (code: string): boolean => me.permissions.includes(code);
    const chat = has('communication.chat');
    const notify = has('communication.notify') || has('communication.notify_own');
    const manage = has('communication.manage');

    return { chat, notify, manage, student: me.population === 'student' || (has('communication.receive') && !chat && !notify && !manage) };
}

/** Une commande de la communication depuis un écran ou un dialogue. */
export function useComCommand(): {
    readonly pending: boolean;
    readonly errors: Errors;
    readonly failure: string | null;
    reset(): void;
    send(action: string, id: string | null, values?: Readonly<Record<string, unknown>>): Promise<CommunicationResult | null>;
} {
    const { t } = useSession();
    const [pending, setPending] = useState(false);
    const [errors, setErrors] = useState<Errors>({});
    const [failure, setFailure] = useState<string | null>(null);

    return {
        pending,
        errors,
        failure,
        reset() {
            setErrors({});
            setFailure(null);
        },
        async send(action, id, values = {}) {
            setPending(true);
            setErrors({});
            setFailure(null);

            try {
                return await runCommunicationCommand(action, id, values);
            } catch (cause) {
                const fields = fieldErrors(t, cause);
                setErrors(fields);
                const general = fields.id ?? fields.target ?? fields.action ?? fields['values.targets'] ?? fields['values.body'];
                setFailure(general ?? Object.values(fields)[0] ?? problemMessage(t, cause));

                return null;
            } finally {
                setPending(false);
            }
        },
    };
}

export function errorOf(errors: Errors, name: string): string | undefined {
    return errors[`values.${name}`];
}

/** La pastille d'un type de notification, dans sa teinte. `Message` pour la messagerie. */
export function TypePill({ type, tone, label }: { readonly type?: TypeRef | null; readonly tone?: Tone; readonly label?: string | undefined }): ReactNode {
    const { t } = useSession();
    const shown = label ?? (type?.code === 'kelassi' ? t('com.type.kelassi') : (type?.label ?? ''));
    const color = tone ?? (type?.code === 'kelassi' ? 'muted' : (type?.tone ?? 'info'));

    return shown === '' ? null : <span className={`com-type com-type--${color}`}>{shown}</span>;
}

/** La pastille d'une personne, ou le « K » de Kelassi pour une alerte du système. */
export function PersonBadge({ person, size = 'md' }: { readonly person: Person | null; readonly size?: 'sm' | 'md' }): ReactNode {
    if (person === null) {
        return (
            <span className={`avatar com-avatar--system${size === 'sm' ? ' com-avatar--sm' : ''}`} aria-hidden="true">
                K
            </span>
        );
    }

    return size === 'sm' ? (
        <span className="avatar com-avatar--sm" aria-hidden="true">
            {person.initials}
        </span>
    ) : (
        <Avatar name={person.name} />
    );
}

function sameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** « il y a 4 min », « 9 h 02 », « hier », « lun. », « 15 oct. » : la date courte d'un élément. */
export function when(iso: string | null, locale: string, t: Translate): string {
    if (iso === null) return '';

    const date = new Date(iso);

    if (Number.isNaN(date.getTime())) return iso;

    const now = new Date();
    const elapsed = (now.getTime() - date.getTime()) / 60_000;
    const lang = intlLocale(locale);

    if (elapsed < 1 && elapsed > -1) return t('com.time.now');

    if (elapsed > 0 && elapsed < 60) return new Intl.RelativeTimeFormat(lang, { numeric: 'auto', style: 'short' }).format(-Math.round(elapsed), 'minute');

    if (sameDay(date, now)) return new Intl.DateTimeFormat(lang, { timeStyle: 'short' }).format(date);

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    if (sameDay(date, yesterday)) return t('com.time.yesterday');

    if (elapsed > 0 && elapsed < 7 * 24 * 60) return new Intl.DateTimeFormat(lang, { weekday: 'short' }).format(date);

    return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'short', ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }) }).format(date);
}

/** L'heure seule, pour une bulle. */
export function clock(iso: string, locale: string): string {
    const date = new Date(iso);

    return Number.isNaN(date.getTime()) ? iso : new Intl.DateTimeFormat(intlLocale(locale), { timeStyle: 'short' }).format(date);
}

/** « Aujourd'hui », « Hier » ou la date : le séparateur de jour d'un fil. */
export function dayLabel(iso: string, locale: string, t: Translate): string {
    const date = new Date(iso);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    if (sameDay(date, now)) return t('com.time.today');
    if (sameDay(date, yesterday)) return t('com.time.yesterday');

    return new Intl.DateTimeFormat(intlLocale(locale), { dateStyle: 'full' }).format(date);
}

export function dayKey(iso: string): string {
    const date = new Date(iso);

    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/** Les pièces jointes d'un message : un clic les télécharge (le jeton ne passe pas dans un lien). */
export function AttachmentList({ items, onRemove }: { readonly items: readonly Attachment[]; onRemove?(id: string): void }): ReactNode {
    const { t, locale } = useSession();

    if (items.length === 0) return null;

    return (
        <ul className="com-files">
            {items.map((item) => (
                <li key={item.id} className="com-file">
                    <button className="com-file__open" type="button" onClick={() => void downloadFile(attachmentPath(item.id), item.name).catch(() => undefined)}>
                        <Icon name="file" size={18} />
                        <span className="com-file__text">
                            <strong>{item.name}</strong>
                            <span className="com-file__meta">
                                {item.mime === 'application/pdf' ? 'PDF' : t('com.file.image')} · {fileSize(item.size, locale)}
                            </span>
                        </span>
                    </button>
                    {onRemove === undefined ? null : (
                        <button className="icon-button com-file__remove" type="button" aria-label={t('com.file.remove', { name: item.name })} data-tip={t('com.file.remove', { name: item.name })} onClick={() => onRemove(item.id)}>
                            <Icon name="close" size={16} />
                        </button>
                    )}
                </li>
            ))}
        </ul>
    );
}

/**
 * Les pièces jointes en cours de rédaction : un champ fichier caché, le dépôt
 * immédiat (`POST /attachments`), la liste à citer par identifiant à l'envoi.
 */
export function useAttachments(owner: AttachmentOwner, initial: readonly Attachment[] = []): {
    readonly items: readonly Attachment[];
    readonly uploading: boolean;
    readonly error: string | null;
    readonly input: ReactNode;
    pick(): void;
    remove(id: string): void;
    clear(): void;
    set(items: readonly Attachment[]): void;
} {
    const { t } = useSession();
    const [items, setItems] = useState<readonly Attachment[]>(initial);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const ref = useRef<HTMLInputElement>(null);

    async function add(files: readonly File[]): Promise<void> {
        setError(null);

        if (items.length + files.length > ATTACHMENTS_MAX) {
            setError(t('com.file.tooMany', { count: ATTACHMENTS_MAX }));

            return;
        }

        const wrong = files.find((file) => !ATTACHMENT_TYPES.includes(file.type));

        if (wrong !== undefined) {
            setError(t('com.file.wrongType', { name: wrong.name }));

            return;
        }

        const big = files.find((file) => file.size > 5 * 1024 * 1024);

        if (big !== undefined) {
            setError(t('com.file.tooBig', { name: big.name }));

            return;
        }

        setUploading(true);

        try {
            const added: Attachment[] = [];

            for (const file of files) {
                added.push(await uploadAttachment(owner, file));
            }

            setItems((current) => [...current, ...added]);
        } catch (cause) {
            setError(problemMessage(t, cause));
        } finally {
            setUploading(false);
        }
    }

    return {
        items,
        uploading,
        error,
        input: (
            <input
                ref={ref}
                className="visually-hidden"
                type="file"
                tabIndex={-1}
                aria-hidden="true"
                accept={ATTACHMENT_TYPES.join(',')}
                multiple
                onChange={(event) => {
                    const files = Array.from(event.target.files ?? []);
                    event.target.value = '';

                    if (files.length > 0) void add(files);
                }}
            />
        ),
        pick() {
            ref.current?.click();
        },
        remove(id) {
            setItems((current) => current.filter((item) => item.id !== id));
        },
        clear() {
            setItems([]);
            setError(null);
        },
        set(next) {
            setItems(next);
        },
    };
}

/** Une bulle de fil : les miennes à droite (dans le sens de lecture), en couleur. */
export function Bubble({
    mine,
    author,
    at,
    body,
    attachments = [],
    receipt,
}: {
    readonly mine: boolean;
    readonly author?: string | null;
    readonly at: string;
    readonly body: string;
    readonly attachments?: readonly Attachment[];
    readonly receipt?: string | null;
}): ReactNode {
    const { locale } = useSession();

    return (
        <div className={`com-bubble${mine ? ' com-bubble--mine' : ''}`}>
            {mine || author === null || author === undefined ? null : (
                <span className="com-bubble__head">
                    {author} · {clock(at, locale)}
                </span>
            )}
            {body === '' ? null : <div className="com-bubble__body">{body}</div>}
            <AttachmentList items={attachments} />
            {mine ? (
                <span className="com-bubble__receipt">
                    {clock(at, locale)}
                    {receipt === null || receipt === undefined ? null : (
                        <>
                            {' · '}
                            <Icon name="checks" size={14} />
                            {receipt}
                        </>
                    )}
                </span>
            ) : null}
        </div>
    );
}

/** Le segment de choix de la maquette (Toutes, Non lues, Groupes…). */
export function Segments<T extends string>({ items, value, label, onChange }: { readonly items: readonly { readonly key: NoInfer<T>; readonly label: string }[]; readonly value: T; readonly label: string; onChange(key: T): void }): ReactNode {
    return (
        <div className="plan-seg com-seg" role="tablist" aria-label={label}>
            {items.map((item) => (
                <button key={item.key} type="button" role="tab" aria-selected={value === item.key} className={`plan-seg__item${value === item.key ? ' plan-seg__item--on' : ''}`} onClick={() => onChange(item.key)}>
                    {item.label}
                </button>
            ))}
        </div>
    );
}

/**
 * La zone de réponse d'un fil : Entrée envoie, Maj + Entrée va à la ligne.
 * `onTyping` est appelé à chaque frappe (l'appelant limite le débit).
 */
export function Composer({
    placeholder,
    owner,
    pending,
    compact = false,
    onSend,
    onTyping,
}: {
    readonly placeholder: string;
    readonly owner: AttachmentOwner | null;
    readonly pending: boolean;
    readonly compact?: boolean;
    onSend(body: string, attachmentIds: readonly string[]): Promise<boolean>;
    onTyping?(): void;
}): ReactNode {
    const { t } = useSession();
    const [body, setBody] = useState('');
    const files = useAttachments(owner ?? 'message');
    const empty = body.trim() === '' && files.items.length === 0;
    // La limite vient de l'entité qui reçoit le texte : une réponse ou un message de discussion.
    const max = (owner === 'reply' ? LIMITS.NotificationReply.body : LIMITS.ChatMessage.body).max ?? undefined;

    const submit = (): void => {
        if (empty || pending || files.uploading) return;

        void onSend(body.trim(), files.items.map((item) => item.id)).then((ok) => {
            if (ok) {
                setBody('');
                files.clear();
            }
        });
    };

    return (
        <div className={`com-composer${compact ? ' com-composer--compact' : ''}`}>
            {files.error === null ? null : <p className="field__error">{files.error}</p>}
            <AttachmentList items={files.items} onRemove={files.remove} />
            <div className="com-composer__row">
                {owner === null ? null : (
                    <>
                        {files.input}
                        <button className="icon-button icon-button--bordered com-composer__attach" type="button" aria-label={t('com.file.attach')} data-tip={t('com.file.attach')} disabled={files.uploading || files.items.length >= ATTACHMENTS_MAX} onClick={files.pick}>
                            <Icon name="paperclip" size={18} />
                        </button>
                    </>
                )}
                <label className="com-composer__field">
                    <span className="visually-hidden">{placeholder}</span>
                    <textarea
                        className="field__input com-composer__input"
                        rows={1}
                        value={body}
                        placeholder={placeholder}
                        maxLength={max}
                        onChange={(event) => {
                            setBody(event.target.value);
                            onTyping?.();
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                                event.preventDefault();
                                submit();
                            }
                        }}
                    />
                </label>
                <button className="button com-composer__send" type="button" disabled={empty || pending || files.uploading} aria-label={t('com.send')} onClick={submit}>
                    <Icon name="send" size={18} />
                    <span className="com-composer__sendText">{t('com.send')}</span>
                </button>
            </div>
        </div>
    );
}
